import { Link } from 'react-router-dom';
import { Diamond, ArrowLeft, ShieldCheck, AlertTriangle, Mail, Clock, FileX2 } from 'lucide-react';

const SECTIONS = [
  {
    id: 'guarantee',
    icon: ShieldCheck,
    title: '7-Day Money-Back Guarantee',
    body: (
      <>
        <p>
          Because the DesignCarat bundle consists of highly premium digital files,
          we face an immense risk of digital theft. However, we stand{' '}
          <span className="font-semibold text-[#111]">100% behind the quality</span>{' '}
          of our files.
        </p>
        <p className="mt-4">
          We offer a strict <span className="font-semibold text-[#111]">7-Day Refund Policy</span>{' '}
          under the following specific conditions:
        </p>
        <ul className="mt-5 space-y-3">
          {[
            'The STL files are fundamentally corrupted or completely un-printable due to mesh errors out of the box.',
            'You notify us within exactly 7 days (168 hours) of your purchase timestamp.',
            'You provide visual proof / screenshots of the slicing software or printer failing due to the mesh geometry.',
          ].map((line, i) => (
            <li key={i} className="flex gap-3">
              <span
                className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full"
                style={{ background: '#FDC3A1' }}
              />
              <span className="font-body text-[15px] text-[#444] leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'exemptions',
    icon: FileX2,
    title: 'Exemptions',
    body: (
      <>
        <p>
          We do <span className="font-semibold text-[#111]">NOT</span> offer refunds for:
        </p>
        <ul className="mt-5 space-y-3">
          {[
            '"I changed my mind."',
            '"I don\u2019t know how to use a 3D printer."',
            '"I thought these were physical rings."',
          ].map((line, i) => (
            <li
              key={i}
              className="flex items-center gap-3 px-4 py-3 rounded-xl"
              style={{
                background: 'rgba(0,0,0,0.025)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <AlertTriangle className="w-4 h-4 text-[#c88a5e] shrink-0" />
              <span className="font-body text-[14.5px] text-[#555] italic">{line}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'request',
    icon: Mail,
    title: 'How to Request a Refund',
    body: (
      <>
        <p>
          To start a refund request, you must email{' '}
          <a
            href="mailto:designcarat9@gmail.com"
            data-testid="refund-email-link"
            className="font-mono font-semibold text-[#111] underline decoration-[#FDC3A1] decoration-2 underline-offset-4 hover:decoration-[#c88a5e] transition-colors"
          >
            designcarat9@gmail.com
          </a>{' '}
          with your <span className="font-semibold text-[#111]">UPI UTR Number</span> and an
          explanation of the technical failure.
        </p>
        <p
          className="mt-5 px-5 py-4 rounded-xl font-body text-[14.5px] text-[#444] leading-relaxed"
          style={{
            background: 'linear-gradient(135deg, #FFF7CD 0%, rgba(253,195,161,0.18) 100%)',
            border: '1px solid rgba(253,195,161,0.45)',
          }}
        >
          <Clock className="inline-block w-4 h-4 -mt-0.5 mr-2 text-[#c88a5e]" />
          If approved, your refund will be processed back to your original UPI account within{' '}
          <span className="font-semibold text-[#111]">5–10 business days</span>.
        </p>
      </>
    ),
  },
];

export default function RefundPolicy() {
  return (
    <main
      data-testid="refund-policy-page"
      className="min-h-screen"
      style={{ background: '#FFF7CD' }}
    >
      {/* ───────── Top bar with back-to-home ───────── */}
      <header
        className="sticky top-0 z-30"
        style={{
          background: 'rgba(255,247,205,0.85)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="refund-back-home"
            className="flex items-center gap-2 font-body text-sm text-[#555] hover:text-[#111] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="flex items-center gap-2">
            <Diamond className="w-4 h-4 text-[#FDC3A1]" />
            <span className="font-heading font-bold text-[#111] text-sm tracking-tight">
              DesignCarat
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        {/* ───────── Title block ───────── */}
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-body text-[10px] font-bold uppercase tracking-[0.22em]"
          style={{
            background: 'rgba(253,195,161,0.18)',
            border: '1px solid rgba(253,195,161,0.5)',
            color: '#c88a5e',
          }}
          data-testid="refund-policy-eyebrow"
        >
          <ShieldCheck className="w-3 h-3" />
          Last Updated: May 2026
        </span>

        <h1
          data-testid="refund-policy-heading"
          className="mt-6 font-heading font-black text-[#111] tracking-tight"
          style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', lineHeight: 1.05 }}
        >
          Refund Policy
        </h1>
        <p className="mt-5 font-body text-base sm:text-lg text-[#666] leading-relaxed max-w-xl">
          Please read this policy carefully before purchasing the DesignCarat bundle.
          By completing checkout, you acknowledge and agree to the terms below.
        </p>

        {/* ───────── Decorative gold divider ───────── */}
        <div
          className="my-12 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(253,195,161,0.7), transparent)',
          }}
        />

        {/* ───────── Sections ───────── */}
        <div className="space-y-12">
          {SECTIONS.map(({ id, icon: Icon, title, body }, idx) => (
            <section key={id} data-testid={`refund-section-${id}`}>
              <div className="flex items-center gap-3 mb-5">
                <span
                  className="flex items-center justify-center w-9 h-9 rounded-full"
                  style={{
                    background: 'rgba(253,195,161,0.2)',
                    border: '1px solid rgba(253,195,161,0.5)',
                  }}
                >
                  <Icon className="w-4 h-4 text-[#c88a5e]" />
                </span>
                <span
                  className="font-mono text-[10px] font-bold uppercase tracking-[0.22em]"
                  style={{ color: '#c88a5e' }}
                >
                  Section {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
              <h2
                className="font-heading font-bold text-[#111] tracking-tight mb-5"
                style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2rem)' }}
              >
                {title}
              </h2>
              <div className="font-body text-[15px] sm:text-base text-[#444] leading-relaxed">
                {body}
              </div>
            </section>
          ))}
        </div>

        {/* ───────── Footer note ───────── */}
        <div
          className="mt-20 pt-8"
          style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
        >
          <p className="font-body text-xs text-[#999]">
            Questions about this policy? Email{' '}
            <a
              href="mailto:designcarat9@gmail.com"
              className="text-[#c88a5e] font-semibold hover:underline"
              data-testid="refund-footer-email"
            >
              designcarat9@gmail.com
            </a>{' '}
            and we&rsquo;ll get back within 24 hours.
          </p>
          <Link
            to="/"
            data-testid="refund-footer-back"
            className="mt-4 inline-flex items-center gap-2 font-body text-sm text-[#555] hover:text-[#111] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to DesignCarat
          </Link>
        </div>
      </div>
    </main>
  );
}
